import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { AnalysisStatus, AnalysisStatusResponseData } from "@/types/scribe";
import { getAnalysisStatus } from "@/api/scribe";
import { auth } from "@/auth";
import type { ScribeEvent } from "@/stores/eventsStore";
import { isAwaitingReport, isFetchingReport, normalizeAnalysisStatus } from "@/components/scribe/utils/analysisStatus";
import { sparkyColdStartDiagnostics } from "@/services/sparkyColdStartDiagnostics";

// Circuit breaker constants
const CIRCUIT_BREAKER_THRESHOLD = 5;
const CIRCUIT_BREAKER_COOLDOWN_MS = 60_000; // 1 minute cooldown before retrying after circuit trips

// Polling cap: minimum interval between refreshes for the same conversation
const POLLING_CAP_MS = 30_000; // 30 seconds

const toComparableTime = (value: Date | string | null | undefined) => {
	if (!value) return null;
	const time = new Date(value).getTime();
	return Number.isNaN(time) ? String(value) : time;
};

const analysisTimeFields = new Set(["createdAt", "updatedAt", "mediaCompleteAt"]);

const hasDefinedAnalysisFieldChange = (
	existing: AnalysisStatusResponseData,
	definedAnalysisFields: Partial<AnalysisStatusResponseData>
) => {
	return Object.entries(definedAnalysisFields).some(([key, value]) => {
		const existingValue = existing[key as keyof AnalysisStatusResponseData];
		if (analysisTimeFields.has(key)) {
			return (
				toComparableTime(existingValue as Date | string | null | undefined) !==
				toComparableTime(value as Date | string | null | undefined)
			);
		}
		return existingValue !== value;
	});
};

// LSP-20159: a report the user has asked for whose create request has not returned yet. contact-api's
// POST /projects/:id/sparks waits on Scribe (p50 2.7s, p99 21s), so the run flow records one of these the
// moment the user commits and the reports list shows "Starting your report…" until the real row lands.
export interface StartingReport {
	clientId: string;
	externalType: "project" | "conversation";
	externalId: string;
	title?: string;
	createdAt: Date;
	// Set once the create request returns; the list hides the placeholder as soon as it has this row.
	analysisId?: string;
}

// Backstop for a resolved placeholder whose row never reaches the list (e.g. deleted in another tab
// before this tab's list refresh). Long enough to cover a slow list refresh, short enough not to linger.
const STARTING_REPORT_RESOLVED_TTL_MS = 60_000;

const hasRunningAnalysis = (analyses: AnalysisStatusResponseData[]) => {
	return analyses.some((analysis) => isFetchingReport(analysis.status, analysis.deferredMedia));
};

export const useScribeStore = defineStore("scribe", () => {
	const currentAnalyses = ref<AnalysisStatusResponseData[]>([]);

	const _externalId = ref(null as string | null);
	const _externalType = ref(null as string | null);
	// LSP-15648: project pages aggregate child conversation reports. Track those child ids so
	// refreshAnalyses monitors the same composite scope the report table shows.
	const _childExternalIds = ref<string[]>([]);
	// LSP-15648: ids deleted via SSE while a composite refresh may be in flight. An older refresh can
	// still return a just-deleted analysis; tombstone it so the stale response cannot re-add it.
	const _recentlyDeletedIds = new Set<string>();
	// LSP-15648: ids of active analyses the UI observed before the status endpoint reflects them.
	// Usually these are just-created analyses added optimistically, but direct navigation after creating
	// a conversation can also observe RUNNING via the report-list endpoint first. Protect them from the
	// scope-aware in-scope drop until a status refresh first returns them; otherwise a stale empty status
	// response clears the shared Sparky running state while the report table still shows Running.
	const _optimisticIds = new Set<string>();
	// LSP-15648: monotonic stamp so overlapping refreshes (SSE event + fallback poll) are last-issued-wins;
	// an older response that resolves after a newer one must not overwrite the fresher state. Hand-rolled
	// (mirrors the `loadSeq` counter in stores/comments.ts) rather than @/composables/useLatestRequest:
	// the composable is a single-request handle, but this store needs a free-standing counter that the SSE
	// handler, markAnalysisCancelling, and reconcileTerminalFromList can all bump without holding a handle.
	let _refreshSequence = 0;

	// LSP-20159: kept OUT of currentAnalyses on purpose. currentAnalyses drives the popup's
	// "Running Your Spark..." + Cancel button, the running dog, status polling and syncStoreFromActiveRows;
	// a placeholder has no analysis id yet, so it must never be cancellable, pollable or counted as running.
	const startingReports = ref<StartingReport[]>([]);
	let _startingReportSequence = 0;

	const hasScribeFeature = ref(true);
	const pollingDisabled = ref((window as any).env?.VITE_DISABLE_SCRIBE_POLLING === "true");

	// Circuit breaker state
	const _consecutiveFailures = ref(0);
	const _circuitTrippedAt = ref<number | null>(null);

	// Polling cap state: last refresh timestamp per conversation key
	const _lastRefreshAt = ref<Map<string, number>>(new Map());

	// Composite scope key (`<externalType>:<externalId>`) — single source of truth for the polling-cap
	// key AND the queriedScope reconciliation in refreshAnalyses, so the format can never silently diverge.
	const scopeKey = (externalType: string | null, externalId: string | null) => `${externalType}:${externalId}`;

	const _getConversationKey = () => scopeKey(_externalType.value, _externalId.value);

	const _isCircuitOpen = (): boolean => {
		if (_consecutiveFailures.value < CIRCUIT_BREAKER_THRESHOLD) return false;
		// Circuit is tripped — check if cooldown has elapsed
		if (_circuitTrippedAt.value && Date.now() - _circuitTrippedAt.value >= CIRCUIT_BREAKER_COOLDOWN_MS) {
			// Cooldown elapsed — allow one call through (half-open state)
			_circuitTrippedAt.value = Date.now();
			return false;
		}
		return true;
	};

	const _isPollingCapped = (): boolean => {
		const key = _getConversationKey();
		const lastRefresh = _lastRefreshAt.value.get(key);
		if (!lastRefresh) return false;
		return Date.now() - lastRefresh < POLLING_CAP_MS;
	};

	const refreshAnalyses = async (bypassPollingCap = false) => {
		// Emergency kill switch — allows disabling all Scribe polling via config
		// But allow SSE-triggered refreshes (bypassPollingCap=true) since those are event-driven, not polling
		if (pollingDisabled.value && !bypassPollingCap) return;

		const externalId = _externalId.value;
		const externalType = _externalType.value;
		if (!externalId || !externalType || !hasScribeFeature.value) return;

		// Circuit breaker — skip if too many consecutive failures (prevents hammering a down API)
		if (_isCircuitOpen()) return;

		// Polling cap — skip if called too recently for this conversation (reduces error volume from stuck tabs)
		// Bypassed for initial fetches (startMonitoring), SSE events, and reconnection recovery
		if (!bypassPollingCap && _isPollingCapped()) return;

		// Stamp this refresh BEFORE the async auth gate (placed after the synchronous cap/circuit guards so
		// a no-op call cannot bump the counter). An invalidation (SSE update, cancel, list reconcile) that
		// arrives while auth is still resolving must supersede this in-flight refresh — capturing the
		// sequence only after the auth await would let a stale refresh re-stamp itself as newest.
		const refreshSequence = ++_refreshSequence;

		const isAuthenticated = await auth.isAuthenticated();
		if (!isAuthenticated) {
			if (sparkyColdStartDiagnostics.claim("scribe_auth_skipped")) {
				sparkyColdStartDiagnostics
					.startPhase("scribe_auth_skipped", {
						routeType: externalType === "project" || externalType === "conversation" ? externalType : "other"
					})
					.finish("skipped");
			}
			// Silent-failure diagnostic: a refresh skipped here leaves the indicator unchanged. Debug-level
			// because token-expiry/cold-start makes this a benign recurring state, not an anomaly to alert on.
			console.debug("ScribeStore: refresh skipped — not authenticated", { externalType, externalId });
			return;
		}

		try {
			// LSP-15648: monitor the composite scope a project page shows — the project itself plus
			// every child conversation report. Querying only the project scope left a running child
			// report either invisible (never entered the store) or stuck RUNNING forever (preserved
			// as optimistic because the project-scope response never includes it).
			// Monitored scopes, primary first then each child conversation — aligned 1:1 with the
			// Promise.allSettled results so one uniform pass handles both.
			const childExternalIds = [..._childExternalIds.value];
			const scopes = [
				{ externalType, externalId, isPrimary: true },
				...childExternalIds.map((childExternalId) => ({
					externalType: "conversation",
					externalId: childExternalId,
					isPrimary: false
				}))
			];
			const responses = await Promise.allSettled(
				scopes.map((scope) => getAnalysisStatus("contact", scope.externalType, scope.externalId))
			);

			const queriedScope = new Set<string>();
			const allData: AnalysisStatusResponseData[] = [];

			responses.forEach((response, index) => {
				const scope = scopes[index]!;
				if (response.status === "fulfilled") {
					// A fulfilled response is authoritative for its scope, INCLUDING a 404 (getAnalysisStatus
					// resolves null) = "no analyses for this scope" → stale entries there must clear.
					queriedScope.add(scopeKey(scope.externalType, scope.externalId));
					if (response.value) {
						allData.push(...response.value.data);
					}
					if (scope.isPrimary) {
						// Reset circuit breaker on success.
						_consecutiveFailures.value = 0;
						_circuitTrippedAt.value = null;
					}
				} else if (scope.isPrimary) {
					console.error("ScribeStore: Error refreshing analyses", response.reason);
					_consecutiveFailures.value++;
					if (_consecutiveFailures.value >= CIRCUIT_BREAKER_THRESHOLD) {
						_circuitTrippedAt.value = Date.now();
					}
				} else {
					// A failed child fetch is NOT authoritative — keep its scope out of queriedScope so
					// existing entries for it are preserved rather than dropped. Log for diagnostics.
					console.warn("ScribeStore: child analysis status refresh failed; preserving its entries", {
						childExternalId: scope.externalId,
						error: response.reason
					});
				}
			});

			// De-duplicate by analysis id — the same child id may be passed more than once (duplicate
			// additionalEntities), and an analysis must never be surfaced or counted twice. Also drop
			// ids tombstoned by an analysis:deleted event so a stale in-flight response cannot re-add them.
			const seenIds = new Set<string>();
			const dedupedData = allData.filter((analysis) => {
				if (seenIds.has(analysis.id)) return false;
				if (_recentlyDeletedIds.has(analysis.id)) return false;
				seenIds.add(analysis.id);
				return true;
			});

			// Once the server reflects an optimistic just-created analysis, drop its protection so normal
			// scope-aware reconciliation applies from here on.
			for (const optimisticId of _optimisticIds) {
				if (seenIds.has(optimisticId)) {
					_optimisticIds.delete(optimisticId);
				}
			}

			// Scope-aware reconciliation: drop an entry the composite response omits ONLY when its
			// scope was queried authoritatively (terminal/gone); preserve genuinely optimistic or
			// out-of-scope entries the server does not yet know about.
			const preserved = currentAnalyses.value.filter((analysis) => {
				if (seenIds.has(analysis.id)) return false;
				// A just-created optimistic entry the server has not yet reflected must survive this refresh.
				if (_optimisticIds.has(analysis.id)) return true;
				const scope = scopeKey(analysis.externalType, analysis.externalId);
				if (queriedScope.has(scope)) {
					console.warn("ScribeStore: dropping stale in-scope analysis omitted from refreshed status", {
						analysisId: analysis.id,
						scope
					});
					return false;
				}
				return true;
			});

			// Bail if a newer refresh was issued while this one was in flight (last-issued-wins) — an older
			// response must never overwrite the fresher state. Debug-level: supersession is normal control
			// flow (overlapping SSE + poll), so this would be noise at warn — but it's the first thing to
			// check if a completed refresh appears to have been discarded.
			if (refreshSequence !== _refreshSequence) {
				console.debug("ScribeStore: discarding superseded refresh response", { refreshSequence, current: _refreshSequence });
				return;
			}

			// Bail if monitoring stopped or switched entities while this refresh was in flight — a late
			// response must never repopulate state for a no-longer-monitored entity (stale-write guard).
			if (_externalId.value !== externalId || _externalType.value !== externalType) {
				console.debug("ScribeStore: discarding refresh for a no-longer-monitored entity", {
					externalType,
					externalId,
					nowType: _externalType.value,
					nowId: _externalId.value
				});
				return;
			}

			currentAnalyses.value = [...dedupedData, ...preserved];
		} catch (error) {
			console.error("ScribeStore: Error refreshing analyses", error);
		} finally {
			// Record refresh timestamp regardless of success/failure to cap polling frequency
			_lastRefreshAt.value.set(_getConversationKey(), Date.now());
		}
	};

	// LSP-15648: bump the refresh sequence so any refresh already in flight (issued before this call)
	// is treated as superseded and cannot overwrite a direct, authoritative mutation of currentAnalyses.
	const _invalidateInFlightRefreshes = () => {
		_refreshSequence++;
	};

	const handleAnalysisEvent = (event: ScribeEvent) => {
		if (!_externalId.value || !_externalType.value) {
			return;
		}

		switch (event.type) {
			case "analysis:updated": {
				const idx = currentAnalyses.value.findIndex((a) => a.id === event.analysisId);
				if (idx !== -1 && event.status) {
					currentAnalyses.value[idx] = {
						...currentAnalyses.value[idx],
						status: event.status as AnalysisStatus,
						updatedAt: new Date()
					};
					// LSP-15648: this SSE push is fresher than any refresh already in flight — invalidate
					// those so a slower, older status response cannot revert this in-place update.
					_invalidateInFlightRefreshes();
				} else if (idx === -1) {
					// Unknown analysis — could belong to this entity; refresh to find out
					void refreshAnalyses(true);
				}
				break;
			}
			case "analysis:created":
				// SSE doesn't include externalId, so we need a full refresh to know if it belongs to this entity
				void refreshAnalyses(true);
				break;
			case "analysis:deleted": {
				// Tombstone the id so an older in-flight composite refresh cannot re-add the deleted analysis.
				if (event.analysisId) {
					_recentlyDeletedIds.add(event.analysisId);
					_optimisticIds.delete(event.analysisId);
				}
				currentAnalyses.value = currentAnalyses.value.filter((a) => a.id !== event.analysisId);
				break;
			}
		}
	};

	const startMonitoring = async (
		externalId: string,
		externalType: string,
		onSuccess?: () => void,
		onError?: (e: string) => void,
		// LSP-15648: child conversation ids to monitor alongside the primary entity (project pages).
		// Optional on purpose: a route-plugin call that omits it must NOT wipe the child scope the
		// report page already set for the same project (that would blind the running indicator).
		childExternalIds?: string[]
	) => {
		// Only clear analyses when monitoring a different entity; preserve optimistic entries for the same entity
		const primaryChanged = _externalId.value !== externalId || _externalType.value !== externalType;
		if (primaryChanged) {
			currentAnalyses.value = [];
			// A new primary entity invalidates tombstones and optimistic ids tracked for the previous one.
			_recentlyDeletedIds.clear();
			_optimisticIds.clear();
		}
		_externalId.value = externalId;
		_externalType.value = externalType;
		if (childExternalIds !== undefined && (childExternalIds.length > 0 || primaryChanged)) {
			// An explicit non-empty child set is authoritative; an empty set is honoured only when the primary
			// changed (a genuinely new entity). LSP-15648: a same-primary caller that passes [] because its
			// children have not loaded yet (e.g. EntityReports mounting on a hard-refreshed Sparky tab before
			// additionalEntities arrive) must NOT clobber the composite scope the page already seeded.
			_childExternalIds.value = [...childExternalIds];
		} else if (childExternalIds === undefined && primaryChanged) {
			// New primary with no child set provided — clear stale children from the previous entity.
			_childExternalIds.value = [];
		}
		// else: same primary with no/empty child set (route plugin, or a not-yet-loaded page) — preserve the
		// existing child scope so the running indicator is not dropped.
		// Emergency kill switch — allows disabling all Scribe polling via config
		if (pollingDisabled.value) return;

		// Initial REST fetch — bypass polling cap since this is a navigation-triggered fetch
		try {
			await refreshAnalyses(true);
			onSuccess?.();
		} catch (e: any) {
			onError?.(e?.message || "Failed to start monitoring");
		}
	};

	// LSP-15648: the run-agent flow adds a just-created analysis optimistically so the running indicator
	// appears instantly. Route it through here (not a raw currentAnalyses push) so the id is protected
	// from the scope-aware in-scope drop until the status endpoint first reflects it.
	const addOptimisticAnalysis = (analysis: AnalysisStatusResponseData) => {
		_optimisticIds.add(analysis.id);
		if (currentAnalyses.value.some((entry) => entry.id === analysis.id)) return;
		currentAnalyses.value = [...currentAnalyses.value, analysis];
	};

	const syncActiveAnalysesFromList = (analyses: AnalysisStatusResponseData[]) => {
		const activeAnalyses = analyses
			.map((analysis) => ({
				...analysis,
				status: normalizeAnalysisStatus(analysis.status) ?? analysis.status
			}))
			.filter((analysis) => isAwaitingReport(analysis.status) && !_recentlyDeletedIds.has(analysis.id));
		if (activeAnalyses.length === 0) return;

		const nextAnalyses = [...currentAnalyses.value];
		let changed = false;

		for (const analysis of activeAnalyses) {
			_optimisticIds.add(analysis.id);
			const existingIndex = nextAnalyses.findIndex((entry) => entry.id === analysis.id);
			if (existingIndex === -1) {
				nextAnalyses.push(analysis);
				changed = true;
				continue;
			}

			const existing = nextAnalyses[existingIndex];
			const definedAnalysisFields = Object.fromEntries(
				Object.entries(analysis).filter(([, value]) => value !== undefined)
			) as Partial<AnalysisStatusResponseData>;
			const next = { ...existing, ...definedAnalysisFields, status: analysis.status };
			if (hasDefinedAnalysisFieldChange(existing, definedAnalysisFields)) {
				nextAnalyses[existingIndex] = next;
				changed = true;
			}
		}

		if (changed) {
			currentAnalyses.value = nextAnalyses;
			_invalidateInFlightRefreshes();
		}
	};

	// LSP-16333: cancellation is requested from several places (EntityReports row,
	// LuckyPackPopup cancel button). Each used to update only its own local state,
	// leaving this store saying RUNNING until the next poll — so the Sparky popup
	// kept showing "Running Your Spark...". Cancel paths call this to sync immediately.
	const markAnalysisCancelling = (analysisId: string) => {
		const analysis = currentAnalyses.value.find((entry) => entry.id === analysisId);
		if (!analysis) {
			console.warn("[ScribeStore] markAnalysisCancelling: analysis not in currentAnalyses — popup state may lag until next refresh", {
				analysisId,
				monitoringId: _externalId.value,
				monitoringType: _externalType.value
			});
			return;
		}
		analysis.status = AnalysisStatus.CANCELLING;
		// LSP-15648: invalidate any in-flight refresh so a stale RUNNING response cannot revert the
		// cancel and re-introduce the LSP-16333 "Running Your Spark..." lag.
		_invalidateInFlightRefreshes();
	};

	// LSP-15648: the report list (results endpoint) can observe a terminal status before the status
	// endpoint catches up. EntityReports.mergeStoreAnalyses calls this to sync the store from an
	// authoritative terminal list row. Invalidating in-flight refreshes ensures an older composite
	// refresh resolving with a stale RUNNING status cannot revert the correction and re-strand the dog.
	const reconcileTerminalFromList = (analysisId: string, status: AnalysisStatus, updatedAt?: Date) => {
		const analysis = currentAnalyses.value.find((entry) => entry.id === analysisId);
		if (!analysis) {
			// Silent-failure diagnostic: the list saw this analysis terminal but the store has no entry to
			// sync, so the correction no-ops and the running indicator can stay stranded. Mirror
			// markAnalysisCancelling's miss log so a recurrence is a Datadog lookup, not archaeology.
			console.warn(
				"[ScribeStore] reconcileTerminalFromList: analysis not in currentAnalyses — running indicator may lag until next refresh",
				{
					analysisId,
					status,
					monitoringId: _externalId.value,
					monitoringType: _externalType.value
				}
			);
			return;
		}
		analysis.status = status;
		if (updatedAt) {
			analysis.updatedAt = updatedAt;
		}
		_optimisticIds.delete(analysisId);
		_invalidateInFlightRefreshes();
	};

	// Drop an analysis from the monitored set once it has been fully cancelled/removed. Unlike
	// markAnalysisCancelling (which leaves a transitional CANCELLING entry), this removes it outright
	// so the popup stops immediately and there is no lingering entry to reconcile on the next poll.
	const removeAnalysis = (analysisId: string) => {
		_optimisticIds.delete(analysisId);
		currentAnalyses.value = currentAnalyses.value.filter((entry) => entry.id !== analysisId);
	};

	const addStartingReport = (report: Pick<StartingReport, "externalType" | "externalId" | "title">): string => {
		_startingReportSequence++;
		const clientId = `starting-report-${Date.now()}-${_startingReportSequence}`;
		startingReports.value = [...startingReports.value, { ...report, clientId, createdAt: new Date() }];
		return clientId;
	};

	const removeStartingReport = (clientId: string) => {
		startingReports.value = startingReports.value.filter((report) => report.clientId !== clientId);
	};

	const resolveStartingReport = (clientId: string, analysisId: string) => {
		const report = startingReports.value.find((entry) => entry.clientId === clientId);
		if (!report) {
			console.warn("[ScribeStore] resolveStartingReport: placeholder not found — it was already removed", { clientId, analysisId });
			return;
		}
		startingReports.value = startingReports.value.map((entry) => (entry.clientId === clientId ? { ...entry, analysisId } : entry));
		setTimeout(() => {
			const expiring = startingReports.value.find((entry) => entry.clientId === clientId);
			if (!expiring) return;
			// Silent-failure diagnostic: the list never showed this analysis, so nothing retired its placeholder.
			console.warn("[ScribeStore] starting-report placeholder expired before its report row appeared in the list", {
				clientId,
				analysisId,
				externalType: expiring.externalType,
				externalId: expiring.externalId
			});
			removeStartingReport(clientId);
		}, STARTING_REPORT_RESOLVED_TTL_MS);
	};

	const stopMonitoring = () => {
		currentAnalyses.value = [];
		_externalId.value = null;
		_externalType.value = null;
		_childExternalIds.value = [];
		_recentlyDeletedIds.clear();
		_optimisticIds.clear();
	};

	// LSP-16333: CANCELLING is excluded so the running-dog animation stops as soon
	// as the user cancels, matching the popup's "Running Your Spark..." state.
	const anyAnalysisIsRunning = computed(() => {
		return hasRunningAnalysis(currentAnalyses.value);
	});

	// Prompt list refresh mechanism
	const promptListRefreshTrigger = ref(0);

	const triggerPromptListRefresh = () => {
		promptListRefreshTrigger.value++;
	};

	const monitoringId = computed(() => _externalId.value);
	const monitoringType = computed(() => _externalType.value);

	return {
		currentAnalyses,
		_optimisticIds,
		addOptimisticAnalysis,
		syncActiveAnalysesFromList,
		startMonitoring,
		stopMonitoring,
		markAnalysisCancelling,
		removeAnalysis,
		reconcileTerminalFromList,
		startingReports,
		addStartingReport,
		resolveStartingReport,
		removeStartingReport,
		anyAnalysisIsRunning,
		monitoringId,
		monitoringType,
		promptListRefreshTrigger,
		triggerPromptListRefresh,
		refreshAnalyses,
		handleAnalysisEvent,
		pollingDisabled
	};
});
